import type * as zod from 'zod';

import { FailureKind } from '@sb/routine-runner/FailureKind';

import type { StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments from './Arguments';
import Variables from './Variables';

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

export default class UpdateVisualizationStep extends Step<
  Arguments,
  Variables
> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    this.variables = {};
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    try {
      let palletizingArgs;
      let positionListArgs;

      if (this.args.targetKind === 'space') {
        // Handle palletizing-specific arguments
        if (this.args.palletizing) {
          let evaluatedLayerIndex;
          let evaluatedBoxIndex;

          if (this.args.palletizing.layerIndex !== undefined) {
            // layer index is 1-based, so we will convert it to 0-based
            evaluatedLayerIndex =
              (await this.routineContext.evaluateExpression(
                this.args.palletizing.layerIndex,
              )) - 1;
          }

          if (this.args.palletizing.boxIndex !== undefined) {
            evaluatedBoxIndex = await this.routineContext.evaluateExpression(
              this.args.palletizing.boxIndex,
            );
          }

          palletizingArgs = {
            scope: this.args.palletizing.scope,
            opacity: this.args.palletizing.opacity,
            labels: this.args.palletizing.labels,
            layerIndex: evaluatedLayerIndex,
            boxIndex: evaluatedBoxIndex,
            palletBaseID: this.args.palletizing.palletBaseID,
          };
        }

        if (this.args.positionList) {
          let evaluatedPositionIndex;

          if (this.args.positionList.positionIndex !== undefined) {
            evaluatedPositionIndex =
              await this.routineContext.evaluateExpression(
                this.args.positionList.positionIndex,
              );
          }

          positionListArgs = {
            positionScope: this.args.positionList.positionScope,
            positionIndex: evaluatedPositionIndex,
          };
        }

        await this.routineContext.updateSpaceItemVisualization(
          this.args.targetSpaceItemId,
          this.args.action,
          palletizingArgs,
          positionListArgs,
        );
      } else if (
        this.args.targetKind === 'workpiece' &&
        this.args.palletizing?.boxTypeID
      ) {
        await this.routineContext.updateWorkpieceVisualization(
          this.args.targetSpaceItemId,
          this.args.action,
          this.args.workpiece?.visualizationType ?? 'tooltip',
          this.args.palletizing?.boxTypeID,
          this.args.workpiece?.tooltipOption,
        );
      }
    } catch (error) {
      return fail({
        failure: { kind: FailureKind.ExecutionFailure },
        failureReason: 'Updating visualization did not work',
        error,
      });
    }
  }
}
